import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { ParticleWaveBackground } from '../lib/ParticleWaveBackground';
import { md } from '../common';

function prefersReducedMotion() {
  if (document.documentElement.classList.contains('is-no-motion')) return true;

  return window.matchMedia('(prefers-reduced-motion: reduce), (update: slow)')
    .matches;
}

export function initWllBook(parent) {
  if (!parent || parent.dataset.wllBookAnimated === 'true') return;

  parent.dataset.wllBookAnimated = 'true';

  if (prefersReducedMotion()) return;

  const isAnimationAllowed = (item) => {
    return !item.classList.contains('animated-gte-md')
      || (window && window.innerWidth >= md);
  }

  if (!isAnimationAllowed(parent)) {
    return;
  }

  const animationEl = parent.querySelector('.wll-book__callout-wave');
  const callout = parent.querySelector('.wll-book__callout');

  if (!animationEl || !callout) return;

  const animation = new ParticleWaveBackground(animationEl, {
    planeWidth: 6,
    planeHeight: 12,
    widthSegments: 80,
    heightSegments: 120,
    particleSize: 0.05,
    waveHeight: 0.3,
    noiseScale: 3,
    animationSpeed: 8000,
    gradient: [
      { color: '#FA937B', stop: 0 },
      { color: '#F6EBE900', stop: 100 },
    ],
    rotation: { x: -0.6, y: 0.09, z: -2.2 },
    cameraPosition: [-1, -2, 3],
    cameraRotation: { x: 0, y: 0, z: 0 },
    autoStart: false,
    hoverRadius: 1,
    hoverStrength: 0.5,
    hoverSpringStiffness: 40,
    hoverSpringDamping: 10,
  });

  const conditionalWaveAnimationStart = () => {
    try {
      if (isAnimationAllowed(animationEl)) {
        animation.startAnimation();
      }
    } catch (e) {
      console.error(e);
      animation.startAnimation();
    }
  }

  ScrollTrigger.create({
    trigger: callout,
    start: 'top bottom',
    end: 'bottom top',
    onEnter: () => conditionalWaveAnimationStart(),
    onLeave: () => animation.stopAnimation(),
    onEnterBack: () => conditionalWaveAnimationStart(),
    onLeaveBack: () => animation.stopAnimation(),
  });

  const details = parent.querySelector('.wll-book__details');
  const items = parent.querySelectorAll('.wll-book__list-item');
  const rule = parent.querySelector('.wll-book__rule');
  const chips = parent.querySelectorAll('.wll-chip');

  if (!details) return;

  const detailsTl = gsap.timeline({
    scrollTrigger: {
      trigger: details,
      start: 'top 75%',
      toggleActions: 'play none none none',
    },
    defaults: { ease: 'power2.out' },
  });

  if (callout) {
    detailsTl.from(callout, {
      opacity: 0,
      duration: 0.7,
    });

    detailsTl.addLabel('wllBookListStart', '-=0.35');
  } else {
    detailsTl.addLabel('wllBookListStart', 0);
  }

  items.forEach((item, index) => {
    const icon = item.querySelector('.wll-book__check');
    const copy = item.querySelector('.wll-book__list-text');
    const position = index === 0 ? 'wllBookListStart' : '>-0.40';

    if (icon) {
      detailsTl.from(
        icon,
        {
          opacity: 0,
          scale: 0.9,
          y: 10,
          rotation: -10,
          filter: 'blur(2px)',
          duration: 0.6,
          ease: 'power2.out',
          clearProps: 'filter',
        },
        position,
      );
    }

    if (copy) {
      detailsTl.fromTo(
        copy,
        {
          opacity: 0,
          y: 10,
          filter: 'blur(2px)',
          clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)',
        },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
          duration: 0.7,
          ease: 'power2.out',
          clearProps: 'filter',
        },
        '<0.04',
      );
    }
  });

  detailsTl.addLabel('wllBookChipsStart', 'wllBookListStart+=0.85');

  if (rule) {
    detailsTl.from(
      rule,
      {
        opacity: 0,
        scaleX: 0,
        transformOrigin: 'left center',
        duration: 0.5,
      },
      'wllBookChipsStart',
    );
  }

  chips.forEach((chip, index) => {
    detailsTl.from(
      chip,
      {
        opacity: 0,
        y: 12,
        scale: 0.94,
        filter: 'blur(4px)',
        duration: 0.5,
        clearProps: 'filter',
      },
      index === 0 ? 'wllBookChipsStart+=0.1' : '<0.1',
    );
  });
}

export function initAllWllBooks(selector = '.wll-book') {
  const blocks = document.querySelectorAll(selector);
  blocks.forEach((block) => initWllBook(block));
}
